import { SECTION_NAMES } from '@bridebook/web/lib/wedding-website/constants';
import { TSectionName } from '@bridebook/web/lib/wedding-website/types';
import { CSSProperties } from 'react';

/**
 * Render-order hooks MotifSections injects onto each section's root element (via cloneElement):
 * - `style` carries the per-section `--i` index var (hero 0, content 1..N) for count-aware CSS effects,
 * - `data-num` is the zero-padded ordinal of non-photo content sections ("01"…) for printed chapter numbers,
 * - `id` is the anchor target for the nav (content sections only).
 * Section components spread these onto their <section>/<header>. See layout-motif/docs/bem-contract.md.
 */
export interface MotifSectionHookProps {
  id?: string;
  style?: CSSProperties;
  'data-num'?: string;
}

/**
 * Anchor ids for the nav links (content sections only; photos have no anchors). `bb-`-prefixed because
 * the site renders inline in the editor document, where bare ids like `venue` could collide with app DOM.
 */
export const MOTIF_SECTION_ID_ENTRIES: ReadonlyArray<readonly [TSectionName, string]> = [
  [SECTION_NAMES.OUR_STORY, 'bb-story'],
  [SECTION_NAMES.SCHEDULE, 'bb-schedule'],
  [SECTION_NAMES.PRIMARY_VENUE, 'bb-venue'],
  [SECTION_NAMES.GIFT_LIST, 'bb-gift'],
  [SECTION_NAMES.HOTELS, 'bb-stay'],
  [SECTION_NAMES.FAQ, 'bb-faq'],
];

export const MOTIF_SECTION_IDS: Record<string, string> =
  Object.fromEntries(MOTIF_SECTION_ID_ENTRIES);

/**
 * Empty decorative slot rendered as the first child of every section (incl. hero and photos) — a pure styling hook a
 * theme can turn into flourishes, rules, number badges, tape on photos… Hidden by default (base CSS `display:none`);
 * a theme opts in by setting `display`. Decorative only: keep it aria-hidden, never put meaning in it.
 */
export const MotifOrnament = () => <div className="bb-ww__ornament" aria-hidden="true" />;
